import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, ArrowRightIcon } from 'lucide-react';
import { TreatmentList } from './TreatmentList';
import { SalonTeam } from './SalonTeam';
import { Faq } from '../shared/Faq';
import { HeadingText } from '../shared/HeadingText';
import { PageHero } from '../shared/PageHero';
import { ParallaxMedia } from '../shared/ParallaxMedia';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { salonFaq } from '../../data/faq';
import { founder, salon } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { usePages, useSite } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { pageActions } from '../../data/pageActions';
import { mediaOr } from '../../core/rules/media';
import { placeholderImages } from '../../data/placeholderImages';
import { shopLabels } from '../../data/shopLabels';
import { btnPrimaryLg, btnSecondaryLg, chipCls } from '../../ui/styles';
import { belowHeader, quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { ClosingBand } from '../shared/ClosingBand';
const categories = ['Anti-ageing', 'Huidverbetering', 'Ontspanning', 'Losse applicaties'] as const;
type Filter = (typeof categories)[number] | 'Alles';
const filters: readonly Filter[] = ['Alles', ...categories];

/**
 * De salonpagina: hier valt de beslissing om te boeken. Volgorde = de vragen van een bezoeker
 * (docs/VORMGEVING.md §3): wie behandelt me en kan ik boeken (hero, sand) · wat kies ik (alle
 * behandelingen, shell; de categorieën blijven bovenin staan) · bij wie (collega's, sand; alleen
 * als Sarah ze op de site zet) · waarom werkt het (bindweefselmassage, klei) · hoe houd ik het
 * vast (van salon naar thuis, sand) · praktisch (vragen, shell) · twijfel (blush).
 * Eén bewegend moment: het beeld van de bindweefselmassage beweegt licht binnen zijn kader.
 */
export function Treatments() {
  const pages = usePages();
  const {
    policy
  } = useSite();
  const {
    openBooking
  } = useOverlayActions();
  const {
    treatments,
    brandNames
  } = useCatalog();
  const [active, setActive] = useState<Filter>('Alles');
  const copy = pages.treatments;
  const {
    bindweefsel
  } = copy;
  // De losse bindweefselmassage: een losse applicatie met bindweefselmassage (in het beheer aan te vinken).
  const bindweefselLos = treatments.find(t => t.bindweefsel && t.category === 'Losse applicaties');
  const heroMedia = mediaOr(copy.hero.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een gezichtsbehandeling`
  });
  // Niet dezelfde foto als de opening: de massage valt terug op haar eigen tijdelijke beeld.
  const massageMedia = mediaOr(bindweefsel.media, {
    image: placeholderImages.massage,
    alt: ''
  });
  const allGroups = categories.map(c => ({
    category: c,
    items: treatments.filter(t => t.category === c)
  })).filter(g => g.items.length > 0);
  // Een categorie zonder behandelingen (alles offline gezet) is geen filter: die gaf een lege pagina.
  const visibleFilters = filters.filter(f => f === 'Alles' || allGroups.some(g => g.category === f));
  const groups = allGroups.filter(g => active === 'Alles' || g.category === active);
  return <>
      <PageHero titleId="salon-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} accentBlock data-id="element-8772" data-mp="2zz3s8x4bgcfl" />} body={copy.hero.body ? fill(copy.hero.body) : undefined} media={heroMedia} byline={{
      lead: <>
              {fill(copy.hero.treatedBy)} <span className="text-ink" data-id="element-8773" data-mp="2r5qqr6s6s2hw">{founder.name}</span>
            </>,
      sub: `${pageActions.byAppointment} · ${policyCopy.experience} ervaring`
    }} actions={(copy.hero.cta || copy.hero.secondaryCta) && <>
              {copy.hero.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-8774" data-mp="2yhwohh9kc9g9">
                  {fill(copy.hero.cta)}
                </button>}
              {copy.hero.secondaryCta && <a href="#behandelingen" className={btnSecondaryLg} data-id="element-8775" data-mp="2pi9w1kytadzc">
                  {fill(copy.hero.secondaryCta)}
                </a>}
            </>}
    // De agenda zit vaak vol: geef wie moet wachten meteen iets te doen.
    footnote={<p data-id="element-8776" data-mp="3m80u1u6baj9w">
            <span className="text-ink" data-id="element-8777" data-mp="3m6fxjz0fsq9e">{fill(copy.hero.agenda.title)}</span> {fill(copy.hero.agenda.body)}{' '}
            {copy.hero.agenda.cta && <Link to="/routines" className="whitespace-nowrap text-ink underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay" data-id="element-8778" data-mp="38e9pl07ftpl1">
                {fill(copy.hero.agenda.cta)}
              </Link>}
          </p>} data-id="element-8771" data-mp="23a5wbbkty8gu" />

      {/* Alle behandelingen, direct na de hero: wie hier komt, wil kiezen. */}
      <Section id="behandelingen" tone="shell" sectionClassName="scroll-mt-24" labelledBy="behandelingen-title" contained={false} data-id="element-8779" data-mp="j3unhhi99aiz">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8780" data-mp="f180pv0fyfsn">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-8781" data-mp="stnwqbuxddk2">
            <h2 id="behandelingen-title" className={`${sectionHeading} text-ink lg:col-span-6`} data-id="element-8782" data-mp="nv4a098ibxqn">
              {fill(copy.listHeading)}
            </h2>
            {copy.includes.length > 0 && <div className="lg:col-span-6" data-id="element-8783" data-mp="3sacd5fy1qnmu">
                <p className="text-[14px] text-ink" data-id="element-8784" data-mp="3sbnivlb1y5gl">{fill(copy.hero.includesTitle)}</p>
                <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5" data-id="element-8785" data-mp="a5sot8lzutyv">
                  {copy.includes.map(item => <li key={item} className="flex items-center gap-1.5 text-[14px] text-muted" data-id="element-8786" data-mp="577ozaanmcay">
                      <CheckIcon className="h-3.5 w-3.5 text-sage" strokeWidth={2} aria-hidden="true" data-id="element-8787" data-mp="1llhck272nx0q" />
                      {item}
                    </li>)}
                </ul>
              </div>}
          </div>

          {/* Eén regel die je kunt schuiven, op elk scherm; blijft net onder de header staan zolang je door de lijst scrolt. */}
          {visibleFilters.length > 2 && <div className={`sticky ${belowHeader} z-sticky -mx-6 mt-10 bg-shell py-3 lg:-mx-10`} data-id="element-8788" data-mp="22kqudp1p7xsc">
              <div className="flex gap-2 overflow-x-auto px-6 [scrollbar-width:none] lg:px-10 [&::-webkit-scrollbar]:hidden" role="group" aria-label="Toon categorie" data-id="element-8789" data-mp="14x0c8wb4i8hw">
                {visibleFilters.map(c => <button key={c} type="button" aria-pressed={active === c} onClick={e => {
              setActive(c);
              // Viel de lijst al onder de balk, dan terug naar het begin van de lijst.
              const list = document.getElementById('behandelingen-lijst');
              const bar = e.currentTarget.parentElement?.parentElement;
              if (list && bar && list.getBoundingClientRect().top < bar.getBoundingClientRect().bottom) {
                window.scrollBy({
                  top: list.getBoundingClientRect().top - bar.getBoundingClientRect().bottom - 8
                });
              }
            }} className={`${chipCls(active === c)} shrink-0`} data-id="element-8790" data-mp="1et3avs6rnp03">
                    {c}
                  </button>)}
              </div>
            </div>}

          <div id="behandelingen-lijst" className="mt-9 space-y-16 lg:space-y-20" data-id="element-8791" data-mp="3n3uokut6qzhz">
            {groups.map(group => <div key={group.category} className="lg:grid lg:grid-cols-12 lg:gap-16" data-id="element-8792" data-mp="3i59oqwhuihu2">
                <h3 className="text-[14px] text-clay lg:col-span-3 lg:pt-8" data-id="element-8793" data-mp="3d3bxbezakgq1">{group.category}</h3>
                <div className="mt-3 lg:col-span-9 lg:mt-0" data-id="element-8794" data-mp="394bgg20djo5o">
                  <TreatmentList items={group.items} data-id="element-8795" data-mp="10uljqckxue73" />
                </div>
              </div>)}
          </div>
        </div>
      </Section>

      <SalonTeam data-id="element-8796" data-mp="15swv4rqvswrf" />

      {/* Bindweefselmassage: waarom het werkt, het ene verhaal op klei */}
      <section className="bg-clay text-shell" aria-labelledby="bindweefsel" data-id="element-8797" data-mp="qvmi3dylbitl">
        <div className="grid lg:grid-cols-12" data-id="element-8798" data-mp="vu7m7ogyqmnb">
          <ParallaxMedia media={massageMedia} sizes="(min-width: 1024px) 42vw, 100vw" range={40} className="aspect-[4/3] w-full sm:aspect-[16/10] lg:order-2 lg:col-span-5 lg:aspect-auto lg:h-full lg:min-h-[680px]" data-id="element-8799" data-mp="vx24pagf1xe3" />

          <div className="px-6 py-20 sm:px-10 lg:order-1 lg:col-span-7 lg:py-28 lg:pl-10 lg:pr-16 xl:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))]" data-id="element-8800" data-mp="877qopkd22s9">
            <p className="text-[14px] text-shell" data-id="element-8801" data-mp="3v9ufhppsnjs0">{bindweefsel.title}</p>
            <h2 id="bindweefsel" className={`mt-5 max-w-xl ${sectionHeading}`} data-id="element-8802" data-mp="3vcqfdjl7cij3">
              {fill(bindweefsel.claim)}
            </h2>
            <p className="mt-7 max-w-lg text-[17px] leading-relaxed text-shell" data-id="element-8803" data-mp="3gmvr63m8h5hi">{fill(bindweefsel.intro)}</p>

            <div className="mt-12 grid gap-10 sm:grid-cols-2 sm:gap-12" data-id="element-8804" data-mp="3bpm6105vnfzh">
              <BulletList title="Voor wie is het goed?" items={bindweefsel.forWho} data-id="element-8805" data-mp="3po3l69hpvk5s" />
              <BulletList title="Wat doet het?" items={bindweefsel.effects} data-id="element-8806" data-mp="3kp8wws0zd3ea" />
            </div>

            {bindweefsel.rhythm && <p className="mt-8 text-[15px] text-shell" data-id="element-8807" data-mp="3gluvy0lzwax">{fill(bindweefsel.rhythm)}</p>}

            {/* Rustige links: de grote boekknoppen staan in de opening en het slot (data/pageSections.ts › bookButton). */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-8" data-id="element-8808" data-mp="c9p762ziq33z">
              {bindweefsel.cta && <button type="button" onClick={() => openBooking(bindweefselLos?.slug)} aria-haspopup="dialog" className={`${quietLink} text-shell underline decoration-shell/40 hover:decoration-shell`} data-id="element-8809" data-mp="89yji85ag33o">
                  {fill(bindweefsel.cta)}
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8810" data-mp="1qqzbk7z0zlvj" />
                </button>}
              {bindweefsel.secondaryCta && <Link to="/webshop" className={`${quietLink} text-shell`} data-id="element-8811" data-mp="1mmrqckrs0o22">
                  {fill(bindweefsel.secondaryCta)}
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8812" data-mp="2egtpksv2zghd" />
                </Link>}
            </div>
          </div>
        </div>
      </section>

      {/* Van salon naar thuis: de brug naar de webshop. Geen tweede groot beeld direct na het
          bewegende moment; tekst links, de merken waar ik mee werk als rustige rij rechts. */}
      <Section tone="sand" labelledBy="salon-bridge" className="grid gap-12 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-8813" data-mp="u0gkovpbbwye">
          <div className="lg:col-span-7" data-id="element-8814" data-mp="p142io9dze25">
            <h2 id="salon-bridge" className={`${sectionHeading} text-ink`} data-id="element-8815" data-mp="p02frmj0d34l">
              <HeadingText heading={copy.bridge.heading} accentBlock data-id="element-8816" data-mp="eyrom7a8wlvz" />
            </h2>
            {copy.bridge.body && <p className="mt-7 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-8817" data-mp="jx1kyl7phsj6">{fill(copy.bridge.body)}</p>}
            {/* Knoppen zonder tekst (leeg gelaten in Paginateksten) niet tonen, zoals op de andere pagina's. */}
            {(copy.bridge.cta || copy.bridge.secondaryCta) && <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-8818" data-mp="xn50yufzsq06">
                {copy.bridge.cta && <Link to="/routines" className={btnPrimaryLg} data-id="element-8819" data-mp="12l6lljt2gxdr">
                    {fill(copy.bridge.cta)}
                  </Link>}
                {copy.bridge.secondaryCta && <Link to="/webshop" className={btnSecondaryLg} data-id="element-8820" data-mp="1ckfk8pyvn06g">
                    {fill(copy.bridge.secondaryCta)}
                  </Link>}
              </div>}
          </div>
          {brandNames.length > 0 && <div className="lg:col-span-5" data-id="element-8821" data-mp="36bcnk7z8rl2a">
              <h3 className="text-[14px] text-muted" data-id="element-8822" data-mp="36c4jjjwr3kt3">In de salon en thuis werk ik met</h3>
              <ul className="mt-4 border-t border-ink/15" data-id="element-8823" data-mp="31ecf5qex6680">
                {brandNames.map(name => <li key={name} className="border-b border-ink/15 py-4 type-name text-ink" data-id="element-8824" data-mp="3gbmxnjpzccd8">
                    {name}
                  </li>)}
              </ul>
            </div>}
      </Section>

      {/* Praktisch */}
      <Section id="salon-vragen" tone="shell" sectionClassName="scroll-mt-24" label={fill(copy.faqTitle)} data-id="element-8825" data-mp="3kbx40xhop4sm">
        <Faq items={salonFaq} title={fill(copy.faqTitle)} data-id="element-8826" data-mp="2vp09dqtw45f" />
      </Section>

      {/* Afsluiter: voor wie nog twijfelt */}
      <ClosingBand tone="blush" titleId="salon-closing" title={<HeadingText heading={copy.closing.heading} data-id="element-8828" data-mp="bvc19yfnb7xy" />} body={<>
            {copy.closing.body && `${fill(copy.closing.body)} `}
            {policyCopy.consultCredit}.
          </>} actions={<>
            {copy.closing.cta && <button type="button" onClick={() => openBooking(policy.consult.slug)} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-8829" data-mp="vhdmghp18m5c">
                {fill(copy.closing.cta)}
              </button>}
            {copy.closing.secondaryCta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={btnSecondaryLg} data-id="element-8830" data-mp="mf4f20p298w6">
                {fill(copy.closing.secondaryCta)}
                <span className="sr-only" data-id="element-8831" data-mp="cjb6stidk1k7">{shopLabels.viaWhatsApp}</span>
              </a>}
          </>} data-id="element-8827" data-mp="3u2i4kqgx4je6" />
    </>;
}
function BulletList({
  title,
  items
}: {
  title: string;
  items: string[];
}) {
  if (items.length === 0) return null;
  return <div data-id="element-8832" data-mp="2qlvbymtqnov">
      <h3 className="type-title" data-id="element-8833" data-mp="3kyl1vhnuk6ih">{title}</h3>
      <ul className="mt-5 divide-y divide-shell/25 border-y border-shell/25" data-id="element-8834" data-mp="3kzw61il7fga4">
        {items.map(item => <li key={item} className="py-4 text-[15px] leading-snug text-shell" data-id="element-8835" data-mp="3px4s3u9h44gn">
            {item}
          </li>)}
      </ul>
    </div>;
}